// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";

import "./styles/global.css";

import HomePage from "./pages/HomePage";
import ExplorePage from "./pages/ExplorePage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentPage from "./pages/PaymentPage";
import StorePage from "./pages/StorePage";
import GoogleCallbackPage from "./pages/GoogleCallbackPage";

import CartScreen from "./components/home/CartScreen";
import Profile from "./components/home/profile";
import Wishlist from "./components/home/Wishlist";
import AddressBook from "./components/home/AddressBook";
import MyOrders from "./components/home/MyOrders";
import MyReviews from "./components/home/MyReviews";
import Messages from "./components/home/Messages";
import Help from "./components/home/Help";

import AuthModal from "./components/common/AuthModal";
import { useAuthModal } from "./hooks/useAuthModal";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import AppLayout from "./components/layout/AppLayout";

import ScrollToTop from "./components/common/ScrollToTop";

import AccountLayout from "./components/account/AccountLayout";
import RecentTab from "./pages/RecentTab";
import VouchersPage from "./components/home/VouchersPage";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Faq from "./pages/Faq";
import ComingSoonSection from "./components/home/ComingSoonSection";
import ShippingPolicy from "./pages/ShippingPolicy";
import ResetPasswordPage from "./pages/ResetPasswordPage";

function AppContent() {
  const { isAuthModalOpen, authModalMessage, hideAuthModal } = useAuthModal();

  return (
    <div className="App">
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/store/:unique_name" element={<StorePage />} />
          <Route path="/product/:slug" element={<ProductDetailPage />} />

          <Route path="/cart" element={<CartScreen />} />
          <Route path="/vouchers" element={<VouchersPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />

          <Route path="/faq" element={<Faq />} />

          <Route path="/account" element={<AccountLayout />}>
            <Route index element={<Navigate to="/account/profile" replace />} />

            <Route path="profile" element={<Profile />} />
            <Route path="wishlist" element={<Wishlist />} />
            <Route path="address-book" element={<AddressBook />} />
            <Route path="orders" element={<MyOrders />} />
            <Route path="reviews" element={<MyReviews />} />
            <Route path="messages" element={<Messages />} />
            <Route path="help" element={<Help />} />

            {/* Later: recent tab component */}
            <Route path="recent" element={<RecentTab />} />
          </Route>
          <Route path="/address-book" element={<AddressBook />} />
          <Route path="/orders" element={<MyOrders />} />
          <Route path="/reviews" element={<MyReviews />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/help" element={<Help />} />
        </Route>

        {/* ✅ ROUTES WITHOUT HEADER (auth pages) */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
        <Route path="/email-verification" element={<EmailVerificationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={2500}
        newestOnTop
        toastStyle={{
          fontFamily: "'Sora', sans-serif",
          fontSize: "13.5px",
          fontWeight: "600",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(14,56,100,0.12)",
        }}
      />

      {/* Global Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={hideAuthModal}
        message={authModalMessage}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          {/* ✅ Ensures every route starts at top */}
          <ScrollToTop />
          <AppContent />
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
